import {
  beginViewportEvidencePreloadRequest,
  isViewportEvidenceNetworkBridgeActive,
} from './viewportEvidenceSessionBridge';

export const CINE_RESOURCE_SPECULATIVE_CONCURRENCY = 2;
export const CINE_RESOURCE_LINKED_INTENT_CONCURRENCY = 3;
export const CINE_RESOURCE_BURST_PROJECTED_CONCURRENCY = 2;
export const CINE_RESOURCE_DECODED_ENTRY_LIMIT = 24;
export const CINE_RESOURCE_DECODED_BYTE_LIMIT = 96 * 1024 * 1024;
export const CINE_RESOURCE_COMPLETED_URL_LIMIT = 4096;
export const CINE_RESOURCE_COMPLETED_URL_BYTE_LIMIT = 2 * 1024 * 1024;
export const CINE_RESOURCE_VISIBLE_PREEMPTION_MARKER = 'KAIROS_PAGE_CINE_VISIBLE_PREEMPTION';
export const CINE_RESOURCE_ROUND_ROBIN_MARKER = 'KAIROS_PAGE_CINE_ROUND_ROBIN';

const normalizedDirection = value => (Number(value) < 0 ? -1 : 1);
const normalizedDistance = value => {
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric >= 0 ? numeric : Number.MAX_SAFE_INTEGER;
};
const normalizedOrder = value => {
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric >= 0 ? numeric : Number.MAX_SAFE_INTEGER;
};
const paneKindKey = (paneId, kind) => `${String(paneId)}:${String(kind)}`;
const EXACT_EVIDENCE_PRELOAD_CONTEXTS = new WeakSet();

export const createCineEvidencePreloadContext = ({
  viewport,
  generation,
  slice,
  phase,
  interaction,
  temperature,
  deliverySource,
  currentOrigin,
  fetchImpl,
  blobFactory,
  createObjectURL,
  revokeObjectURL,
  createImage,
  isMounted,
  isViewportCurrent,
  isGenerationCurrent,
  isSliceCurrent,
  isRenderAuthorityCurrent,
} = {}) => {
  const context = Object.freeze({
    viewport,
    generation,
    slice,
    phase,
    interaction,
    temperature,
    deliverySource,
  });
  const evidencePreloadContext = Object.freeze({
    context,
    currentOrigin,
    fetchImpl,
    blobFactory,
    createObjectURL,
    revokeObjectURL,
    createImage,
    isMounted,
    isViewportCurrent,
    isGenerationCurrent,
    isSliceCurrent,
    isRenderAuthorityCurrent,
  });
  EXACT_EVIDENCE_PRELOAD_CONTEXTS.add(evidencePreloadContext);
  return evidencePreloadContext;
};

const inspectCandidateEvidencePreloadContext = candidate => {
  try {
    if (!candidate || (typeof candidate !== 'object' && typeof candidate !== 'function')) {
      return { supplied: false, snapshot: null };
    }
    const descriptor = Object.getOwnPropertyDescriptor(candidate, 'evidencePreloadContext');
    if (!descriptor) return { supplied: false, snapshot: null };
    if (!Object.prototype.hasOwnProperty.call(descriptor, 'value')) return { supplied: true, snapshot: null };
    if (!EXACT_EVIDENCE_PRELOAD_CONTEXTS.has(descriptor.value)) {
      return { supplied: true, snapshot: null };
    }
    return { supplied: true, snapshot: descriptor.value };
  } catch (_) {
    return { supplied: true, snapshot: null };
  }
};
const safelyCancelStreamReader = reader => {
  try {
    Promise.resolve(reader?.cancel?.()).catch(() => {});
  } catch (_) {}
};
const utf8ByteLength = value => {
  const text = String(value || '');
  let bytes = 0;
  for (let index = 0; index < text.length; index += 1) {
    const code = text.charCodeAt(index);
    if (code <= 0x7f) bytes += 1;
    else if (code <= 0x7ff) bytes += 2;
    else if (code >= 0xd800 && code <= 0xdbff && text.charCodeAt(index + 1) >= 0xdc00 && text.charCodeAt(index + 1) <= 0xdfff) {
      bytes += 4;
      index += 1;
    } else bytes += 3;
  }
  return bytes;
};

export const createCineResourceGovernor = ({
  fetchImpl = (...args) => window.fetch(...args),
  createAbortController = () => new window.AbortController(),
  createSpeculativeImage = () => new Image(),
  speculativeConcurrency = CINE_RESOURCE_SPECULATIVE_CONCURRENCY,
  decodedEntryLimit = CINE_RESOURCE_DECODED_ENTRY_LIMIT,
  decodedByteLimit = CINE_RESOURCE_DECODED_BYTE_LIMIT,
  completedUrlLimit = CINE_RESOURCE_COMPLETED_URL_LIMIT,
  completedUrlByteLimit = CINE_RESOURCE_COMPLETED_URL_BYTE_LIMIT,
  onAuthorizationFailure,
  scheduleDrain = callback => window.setTimeout(callback, 0),
  isEvidencePreloadActive = isViewportEvidenceNetworkBridgeActive,
  beginEvidencePreloadRequest = beginViewportEvidencePreloadRequest,
} = {}) => {
  const concurrency = Math.max(1, Math.floor(Number(speculativeConcurrency) || CINE_RESOURCE_SPECULATIVE_CONCURRENCY));
  const entryLimit = Math.max(0, Math.floor(Number(decodedEntryLimit) || 0));
  const byteLimit = Math.max(0, Number(decodedByteLimit) || 0);
  const completedEntryLimit = Math.max(0, Math.floor(Number(completedUrlLimit) || 0));
  const completedByteLimit = Math.max(0, Math.floor(Number(completedUrlByteLimit) || 0));
  const paneState = new Map();
  const visibleByPane = new Map();
  const pendingByUrl = new Map();
  const activeByUrl = new Map();
  const activeJobs = new Set();
  const linkedIntentTransfers = new Set();
  const unsettledByUrl = new Map();
  const claimsByPaneKind = new Map();
  const completedUrls = new Map();
  let completedUrlBytes = 0;
  const decoded = new Map();
  const notifiedPreloadRoots = new WeakSet();
  const failedRootNotifications = new Map();
  // At most entryLimit decoded generations can be current. One additional slot
  // contains the candidate generation while victims move atomically from current
  // ownership into retryable postcommit cleanup.
  const cleanupCapacity = Math.max(
    1,
    Math.min(Number.MAX_SAFE_INTEGER, entryLimit + 1),
  );
  let rootCleanupInProgress = false;
  let decodedBytes = 0;
  let cacheHits = 0;
  let cacheMisses = 0;
  let drainScheduled = false;
  let speculativeEpoch = 0;
  let lastServedPane = null;
  let aborted = 0;
  let deduplicated = 0;
  let destroyed = false;
  let jobSequence = 0;

  const clearCompletedUrls = () => {
    completedUrls.clear();
    completedUrlBytes = 0;
  };

  const completedRecordBytes = record => (
    typeof record === 'number' ? record : record?.bytes
  );

  const removeCompletedUrl = (key, expectedRecord = null) => {
    const record = completedUrls.get(key);
    if (record == null || (expectedRecord != null && record !== expectedRecord)) return false;
    completedUrls.delete(key);
    completedUrlBytes -= completedRecordBytes(record) || 0;
    return true;
  };

  const isLiveBenchmarkCompletion = (key, record) => {
    if (!record || typeof record !== 'object' || record.kind !== 'benchmark-root') return false;
    const entry = decoded.get(key);
    return entry != null
      && entry.preloadManaged === true
      && entry.generation === record.decodedGeneration
      && entry.evidenceRootGeneration === record.rootGeneration
      && entry.image === record.image
      && entry.evidenceRoot === record.root
      && entry.evidenceRoot != null
      && entry.evidenceRootNotified === false;
  };

  const invalidateBenchmarkCompletion = entry => {
    if (!entry || typeof entry.completionKey !== 'string') return false;
    const record = completedUrls.get(entry.completionKey);
    if (!record || typeof record !== 'object' || record.kind !== 'benchmark-root'
        || record.decodedGeneration !== entry.generation
        || record.rootGeneration !== entry.evidenceRootGeneration
        || record.image !== entry.image || record.root !== entry.evidenceRoot) return false;
    return removeCompletedUrl(entry.completionKey, record);
  };

  // benchmarkEntry === undefined preserves the legacy encoded-URL completion contract.
  // A supplied entry binds completion to that exact private decoded/root generation.
  const rememberCompletedUrl = (url, benchmarkEntry = undefined) => {
    const key = String(url || '');
    if (!key) return false;
    const bytes = utf8ByteLength(key);
    if (benchmarkEntry !== undefined) {
      if (!benchmarkEntry || decoded.get(key) !== benchmarkEntry
          || benchmarkEntry.completionKey !== key
          || benchmarkEntry.preloadManaged !== true
          || benchmarkEntry.evidenceRoot == null
          || benchmarkEntry.evidenceRootGeneration == null
          || benchmarkEntry.evidenceRootNotified) return false;
    }
    removeCompletedUrl(key);
    if (completedEntryLimit === 0 || completedByteLimit === 0 || bytes > completedByteLimit) return false;
    completedUrls.set(key, benchmarkEntry === undefined ? bytes : {
      kind: 'benchmark-root',
      bytes,
      decodedGeneration: benchmarkEntry.generation,
      rootGeneration: benchmarkEntry.evidenceRootGeneration,
      image: benchmarkEntry.image,
      root: benchmarkEntry.evidenceRoot,
    });
    completedUrlBytes += bytes;
    while (completedUrls.size > completedEntryLimit || completedUrlBytes > completedByteLimit) {
      const oldest = completedUrls.entries().next().value;
      if (!oldest) break;
      removeCompletedUrl(oldest[0], oldest[1]);
    }
    return completedUrls.has(key);
  };

  const hasCompletedUrl = url => {
    const key = String(url || '');
    const record = completedUrls.get(key);
    if (record == null) return false;
    if (typeof record === 'object' && !isLiveBenchmarkCompletion(key, record)) {
      removeCompletedUrl(key, record);
      return false;
    }
    completedUrls.delete(key);
    completedUrls.set(key, record);
    return true;
  };

  const releaseDetachedImage = image => {
    if (!image) return false;
    try { image.onload = null; } catch (_) {}
    try { image.onerror = null; } catch (_) {}
    try { image.removeAttribute?.('src'); } catch (_) {}
    try { image.src = ''; } catch (_) {}
    return true;
  };

  const evidenceRootEvictor = candidate => {
    try {
      if (!candidate || (typeof candidate !== 'object' && typeof candidate !== 'function')) return null;
      const descriptor = Object.getOwnPropertyDescriptor(candidate, 'evict');
      if (!descriptor || !Object.prototype.hasOwnProperty.call(descriptor, 'value')
          || typeof descriptor.value !== 'function') return null;
      return descriptor.value;
    } catch (_) {
      return null;
    }
  };

  const activeCleanupCount = () => {
    let count = 0;
    decoded.forEach(entry => {
      if (entry?.evidenceRoot != null && !entry.evidenceRootNotified) count += 1;
    });
    return count;
  };

  const cleanupResult = (attempted, released) => Object.freeze({
    attempted,
    released,
    pending: failedRootNotifications.size,
  });

  const preserveFailedRootNotification = notification => {
    if (!notification?.generation || typeof notification.evict !== 'function') return false;
    if (failedRootNotifications.has(notification.generation)) return true;
    if (failedRootNotifications.size >= cleanupCapacity) return false;
    failedRootNotifications.set(notification.generation, notification);
    return true;
  };

  const attemptRootNotifications = generations => {
    if (rootCleanupInProgress) return cleanupResult(0, 0);
    const pendingGenerations = [...new Set(generations)]
      .filter(generation => failedRootNotifications.has(generation));
    if (pendingGenerations.length === 0) return cleanupResult(0, 0);
    rootCleanupInProgress = true;
    let attempted = 0;
    let released = 0;
    try {
      pendingGenerations.forEach(generation => {
        const notification = failedRootNotifications.get(generation);
        if (!notification) return;
        attempted += 1;
        try {
          Reflect.apply(notification.evict, notification.root, []);
          if (failedRootNotifications.get(generation) === notification) {
            failedRootNotifications.delete(generation);
            released += 1;
          }
        } catch (_) {}
      });
    } finally {
      rootCleanupInProgress = false;
    }
    return cleanupResult(attempted, released);
  };

  const preserveAndAttemptRootNotifications = notifications => {
    const generations = [];
    notifications.forEach(notification => {
      if (preserveFailedRootNotification(notification)) generations.push(notification.generation);
    });
    return attemptRootNotifications(generations);
  };

  const retryRootCleanup = (...args) => {
    if (args.length !== 0 || rootCleanupInProgress) return cleanupResult(0, 0);
    return attemptRootNotifications([...failedRootNotifications.keys()]);
  };

  const hasCleanupCapacityForNewGeneration = () => {
    return activeCleanupCount() + failedRootNotifications.size + 1 <= cleanupCapacity;
  };

  const takeDecodedEvidenceRootNotification = entry => {
    if (!entry || entry.evidenceRoot == null || entry.evidenceRootNotified) return null;
    const notification = {
      generation: entry.evidenceRootGeneration || Symbol('decoded-evidence-root-generation'),
      root: entry.evidenceRoot,
      evict: entry.evidenceRootEvict,
    };
    entry.evidenceRoot = null;
    entry.evidenceRootEvict = null;
    entry.evidenceRootGeneration = null;
    entry.evidenceRootNotified = true;
    if (entry.preloadOwned) notifiedPreloadRoots.add(notification.root);
    return notification;
  };

  const notifyDecodedEvidenceRoot = entry => {
    const notification = takeDecodedEvidenceRootNotification(entry);
    if (!notification) return false;
    preserveAndAttemptRootNotifications([notification]);
    return true;
  };

  const decodedByteEstimate = image => {
    try {
      if (!image || (typeof image !== 'object' && typeof image !== 'function')) return null;
      const widthDescriptor = Object.getOwnPropertyDescriptor(image, 'naturalWidth');
      const heightDescriptor = Object.getOwnPropertyDescriptor(image, 'naturalHeight');
      if ((widthDescriptor && !Object.prototype.hasOwnProperty.call(widthDescriptor, 'value'))
          || (heightDescriptor && !Object.prototype.hasOwnProperty.call(heightDescriptor, 'value'))) return null;
      const width = Reflect.get(image, 'naturalWidth');
      const height = Reflect.get(image, 'naturalHeight');
      if ((widthDescriptor && width !== widthDescriptor.value)
          || (heightDescriptor && height !== heightDescriptor.value)) return null;
      if (typeof width !== 'number' || typeof height !== 'number') return null;
      if (!Number.isSafeInteger(width) || width <= 0 || !Number.isSafeInteger(height) || height <= 0) return null;
      const bytes = width * height * 4;
      return Number.isSafeInteger(bytes) && bytes > 0 ? bytes : null;
    } catch (_) {
      return null;
    }
  };

  const isCanonicalDecodedKey = key => (
    typeof key === 'string'
    && key.length > 0
    && key === key.trim()
  );

  const commitDecodedPlan = ({ planned, plannedBytes, victims }) => {
    const notifications = [];
    decoded.clear();
    planned.forEach(([key, entry]) => decoded.set(key, entry));
    decodedBytes = plannedBytes;
    victims.forEach(victim => {
      invalidateBenchmarkCompletion(victim);
      const notification = takeDecodedEvidenceRootNotification(victim);
      if (notification) notifications.push(notification);
      if (victim.preloadOwned || victim.preloadManaged) {
        victim.preloadOwned = false;
        victim.preloadDetached = true;
      }
    });
    preserveAndAttemptRootNotifications(notifications);
    const retainedImages = new Set([...decoded.values()].map(entry => entry.image));
    victims.forEach(victim => {
      if (!victim.preloadManaged && !retainedImages.has(victim.image)) releaseDetachedImage(victim.image);
    });
  };

  const retainDecoded = ({ url, image } = {}) => {
    const key = String(url || '');
    const bytes = decodedByteEstimate(image);
    if (destroyed) return { retained: false, bytes: bytes || 0, reason: 'destroyed' };
    if (!key || bytes == null) return { retained: false, bytes: bytes || 0, reason: 'invalid' };
    if (entryLimit === 0 || byteLimit === 0 || bytes > byteLimit) {
      return { retained: false, bytes, reason: 'oversize' };
    }

    const priorEntry = decoded.get(key);
    const entry = {
      image,
      bytes,
      evidenceRoot: null,
      evidenceRootEvict: null,
      evidenceRootGeneration: null,
      evidenceRootNotified: false,
      generation: Symbol('decoded-generation'),
    };
    const planned = [...decoded.entries()].filter(([currentKey]) => currentKey !== key);
    planned.push([key, entry]);
    let plannedBytes = decodedBytes - (priorEntry?.bytes || 0) + bytes;
    const victims = new Set(priorEntry ? [priorEntry] : []);
    while (planned.length > entryLimit || plannedBytes > byteLimit) {
      const oldest = planned.shift();
      if (!oldest) return { retained: false, bytes, reason: 'oversize' };
      victims.add(oldest[1]);
      plannedBytes -= oldest[1].bytes;
    }
    if (!planned.some(([, plannedEntry]) => plannedEntry === entry)) {
      return { retained: false, bytes, reason: 'oversize' };
    }

    commitDecodedPlan({ planned, plannedBytes, victims });
    return { retained: decoded.get(key) === entry, bytes, reason: decoded.get(key) === entry ? 'retained' : 'oversize' };
  };

  const getDecoded = url => {
    if (destroyed) return null;
    const key = String(url || '');
    const entry = decoded.get(key);
    if (!entry) {
      cacheMisses += 1;
      return null;
    }
    decoded.delete(key);
    decoded.set(key, entry);
    cacheHits += 1;
    return entry.image;
  };

  const registerDecodedEvidenceRoot = (url, evidenceRoot) => {
    if (destroyed || typeof url !== 'string' || !url) return false;
    const entry = decoded.get(url);
    const evict = evidenceRootEvictor(evidenceRoot);
    if (!entry || !evict) return false;
    if (entry.evidenceRoot === evidenceRoot && !entry.evidenceRootNotified) return true;
    if (!hasCleanupCapacityForNewGeneration()) return false;
    invalidateBenchmarkCompletion(entry);
    const priorNotification = takeDecodedEvidenceRootNotification(entry);
    entry.evidenceRoot = evidenceRoot;
    entry.evidenceRootEvict = evict;
    entry.evidenceRootGeneration = Symbol('decoded-evidence-root-generation');
    entry.evidenceRootNotified = false;
    preserveAndAttemptRootNotifications(priorNotification ? [priorNotification] : []);
    return true;
  };

  // A returned detacher is the ownership-transfer commit: this governor then owns
  // both image and evidenceRoot. null is a precommit rejection; the caller keeps
  // ownership and this governor neither retains nor invokes the candidate root.
  const registerPreloadedDecodedEvidenceRoot = (url, image, evidenceRoot) => {
    const evict = evidenceRootEvictor(evidenceRoot);
    if (!evict) return null;

    const bytes = decodedByteEstimate(image);
    if (destroyed || !isCanonicalDecodedKey(url)
        || bytes == null || entryLimit === 0 || byteLimit === 0 || bytes > byteLimit) return null;

    const priorEntry = decoded.get(url);
    if (priorEntry?.preloadOwned && priorEntry.image === image
        && priorEntry.evidenceRoot === evidenceRoot && !priorEntry.evidenceRootNotified
        && typeof priorEntry.preloadDetach === 'function') return priorEntry.preloadDetach;

    for (const current of decoded.values()) {
      if (current.evidenceRoot === evidenceRoot && !current.evidenceRootNotified) return null;
    }
    if (notifiedPreloadRoots.has(evidenceRoot)) return null;
    if (!hasCleanupCapacityForNewGeneration()) return null;

    const rootGeneration = Symbol('preloaded-evidence-root-generation');
    const entry = {
      image,
      bytes,
      evidenceRoot,
      evidenceRootEvict: evict,
      evidenceRootGeneration: rootGeneration,
      evidenceRootNotified: false,
      preloadOwned: true,
      preloadManaged: true,
      preloadDetached: false,
      preloadDetach: null,
      completionKey: url,
      generation: Symbol('preloaded-decoded-generation'),
    };
    const planned = [...decoded.entries()].filter(([key]) => key !== url);
    planned.push([url, entry]);
    let plannedBytes = decodedBytes - (priorEntry?.bytes || 0) + bytes;
    const victims = new Set(priorEntry ? [priorEntry] : []);
    while (planned.length > entryLimit || plannedBytes > byteLimit) {
      const oldest = planned.shift();
      if (!oldest) return null;
      victims.add(oldest[1]);
      plannedBytes -= oldest[1].bytes;
    }
    if (!planned.some(([, plannedEntry]) => plannedEntry === entry)) return null;

    entry.preloadDetach = (...args) => {
      if (args.length !== 0 || entry.preloadDetached) return false;
      const current = decoded.get(url);
      if (current !== entry || current.generation !== entry.generation
          || current.image !== image || current.evidenceRoot !== evidenceRoot
          || current.evidenceRootNotified) {
        entry.preloadDetached = true;
        return false;
      }
      entry.preloadDetached = true;
      decoded.delete(url);
      decodedBytes -= entry.bytes;
      invalidateBenchmarkCompletion(entry);
      entry.evidenceRoot = null;
      entry.evidenceRootEvict = null;
      entry.evidenceRootGeneration = null;
      entry.evidenceRootNotified = true;
      entry.preloadOwned = false;
      notifiedPreloadRoots.add(evidenceRoot);
      return true;
    };

    commitDecodedPlan({ planned, plannedBytes, victims });
    return entry.preloadDetach;
  };

  const getDecodedEvidenceRoot = url => {
    if (destroyed || typeof url !== 'string' || !url) return null;
    const entry = decoded.get(url);
    if (!entry || entry.evidenceRoot == null || entry.evidenceRootNotified) return null;
    decoded.delete(url);
    decoded.set(url, entry);
    return entry.evidenceRoot;
  };

  const evictDecodedEvidenceRoot = url => {
    if (destroyed || typeof url !== 'string' || !url) return false;
    const entry = decoded.get(url);
    if (!entry) return false;
    if (!entry.preloadManaged) return notifyDecodedEvidenceRoot(entry);
    decoded.delete(url);
    decodedBytes -= entry.bytes;
    invalidateBenchmarkCompletion(entry);
    entry.preloadOwned = false;
    entry.preloadDetached = true;
    return notifyDecodedEvidenceRoot(entry);
  };

  const cancelBridgeOperation = job => {
    if (!job?.bridgeOperation || job.bridgeCancelRequested) return false;
    job.bridgeCancelRequested = true;
    try { job.bridgeOperation.cancel(); } catch (_) {}
    return true;
  };

  const abortSpeculativeJob = job => {
    if (!job || job.cancelled) return false;
    job.cancelled = true;
    if (typeof job.cancelIntentImage === 'function') {
      const cancelIntentImage = job.cancelIntentImage;
      job.cancelIntentImage = null;
      cancelIntentImage();
    } else if (job.intentImage) {
      const image = job.intentImage;
      job.intentImage = null;
      releaseDetachedImage(image);
    }
    safelyCancelStreamReader(job.reader);
    cancelBridgeOperation(job);
    try { job.controller?.abort(); } catch (_) {}
    aborted += 1;
    return true;
  };

  const removeClaim = claim => {
    const job = pendingByUrl.get(claim.url) || activeByUrl.get(claim.url);
    job?.claims.delete(claim.id);
    const keyClaims = claimsByPaneKind.get(claim.paneKind);
    keyClaims?.delete(claim);
    if (keyClaims && keyClaims.size === 0) claimsByPaneKind.delete(claim.paneKind);
    if (!job) return;
    if (job.claims.size > 0) {
      if (activeByUrl.get(claim.url) === job && job.preloadProducerClaim === claim) {
        const replacement = {
          url: job.url,
          claims: new Map(job.claims),
          sequence: jobSequence += 1,
          controller: null,
          cancelled: false,
        };
        job.claims.clear();
        pendingByUrl.set(job.url, replacement);
        abortSpeculativeJob(job);
      }
      return;
    }
    if (pendingByUrl.get(claim.url) === job) pendingByUrl.delete(claim.url);
    if (activeByUrl.get(claim.url) === job) {
      abortSpeculativeJob(job);
    }
  };

  const removeSpeculative = ({ paneId, kind } = {}) => {
    if (destroyed) return;
    for (const transfer of [...linkedIntentTransfers]) {
      if (transfer.paneId === paneId && transfer.kind === kind) transfer.cancel();
    }
    const key = paneKindKey(paneId, kind);
    for (const claim of [...(claimsByPaneKind.get(key) || [])]) removeClaim(claim);
    requestDrain();
  };

  const clearSpeculative = () => {
    speculativeEpoch += 1;
    for (const transfer of [...linkedIntentTransfers]) transfer.cancel();
    for (const job of activeJobs) {
      abortSpeculativeJob(job);
    }
    pendingByUrl.clear();
    activeByUrl.clear();
    claimsByPaneKind.clear();
    drainScheduled = false;
    lastServedPane = null;
  };

  const preemptSpeculativeExceptKinds = kinds => {
    const preserved = new Set(Array.isArray(kinds) ? kinds.map(String) : []);
    if (preserved.size === 0) {
      clearSpeculative();
      return;
    }
    for (const transfer of [...linkedIntentTransfers]) {
      if (!preserved.has(String(transfer.kind))) transfer.cancel();
    }
    const claims = [...claimsByPaneKind.values()].flatMap(keyClaims => [...keyClaims]);
    claims.forEach(claim => {
      if (!preserved.has(String(claim.kind))) removeClaim(claim);
    });
    requestDrain();
  };

  const removePaneClaims = paneId => {
    for (const transfer of [...linkedIntentTransfers]) {
      if (transfer.paneId === paneId) transfer.cancel();
    }
    for (const key of [...claimsByPaneKind.keys()]) {
      if (key.startsWith(`${String(paneId)}:`)) {
        for (const claim of [...(claimsByPaneKind.get(key) || [])]) removeClaim(claim);
      }
    }
  };

  const fencePaneGeneration = ({ paneId, generation, direction = 1 } = {}) => {
    if (destroyed || paneId === undefined || paneId === null || !Number.isSafeInteger(generation)) return false;
    removePaneClaims(paneId);
    visibleByPane.delete(paneId);
    paneState.set(paneId, {
      ...(paneState.get(paneId) || {}),
      generation,
      direction: normalizedDirection(direction),
    });
    requestDrain();
    return true;
  };

  const registerPane = (paneId, callbacks = {}) => {
    if (destroyed) return () => {};
    const registration = {
      clearVisible: typeof callbacks.clearVisible === 'function' ? callbacks.clearVisible : null,
      pauseFullWarmup: typeof callbacks.pauseFullWarmup === 'function' ? callbacks.pauseFullWarmup : null,
    };
    paneState.set(paneId, { ...(paneState.get(paneId) || {}), registration });
    let registered = true;
    return () => {
      if (!registered) return;
      registered = false;
      const state = paneState.get(paneId);
      if (state?.registration !== registration) return;
      try { registration.pauseFullWarmup?.(); } catch (_) {}
      removePaneClaims(paneId);
      visibleByPane.delete(paneId);
      paneState.delete(paneId);
      requestDrain();
    };
  };

  const releaseAllDecoded = () => {
    const entries = [...decoded.values()];
    const notifications = [];
    decoded.clear();
    decodedBytes = 0;
    entries.forEach(entry => {
      invalidateBenchmarkCompletion(entry);
      const notification = takeDecodedEvidenceRootNotification(entry);
      if (notification) notifications.push(notification);
      if (entry.preloadOwned || entry.preloadManaged) {
        entry.preloadOwned = false;
        entry.preloadDetached = true;
      }
    });
    preserveAndAttemptRootNotifications(notifications);
    const retainedImages = new Set([...decoded.values()].map(entry => entry.image));
    entries.forEach(entry => {
      if (!entry.preloadManaged && !retainedImages.has(entry.image)) releaseDetachedImage(entry.image);
    });
  };

  const resetState = ({ destroying = false } = {}) => {
    clearSpeculative();
    visibleByPane.clear();
    clearCompletedUrls();
    const registrations = [...paneState.entries()].map(([paneId, state]) => [paneId, state.registration]);
    if (destroying) paneState.clear();
    else registrations.forEach(([paneId, registration]) => paneState.set(paneId, { registration }));
    releaseAllDecoded();
    registrations.forEach(([, registration]) => {
      try { registration?.pauseFullWarmup?.(); } catch (_) {}
      try { registration?.clearVisible?.(); } catch (_) {}
    });
  };

  const reset = () => {
    if (destroyed) return;
    resetState();
  };

  const destroy = () => {
    if (destroyed) return;
    destroyed = true;
    resetState({ destroying: true });
  };

  const claimIsCurrent = claim => {
    const state = paneState.get(claim.paneId);
    if (!state) return false;
    if (state.generation === claim.generation) return true;
    return claim.kind === 'burst-projected'
      && state.direction === claim.direction;
  };
  const validClaimsFor = job => [...job.claims.values()].filter(claimIsCurrent);

  const takeLinkedIntentImage = ({ paneId, url } = {}) => {
    if (destroyed || paneId === undefined || paneId === null) return null;
    const key = String(url || '');
    const job = activeByUrl.get(key);
    if (!key || !job || job.cancelled || job.runKind !== 'linked-intent'
        || !job.intentImage || job.intentTransfer) return null;
    const validClaims = validClaimsFor(job);
    const exactClaim = validClaims.find(claim => (
      claim.paneId === paneId && claim.kind === 'linked-intent' && claim.url === key
    ));
    // An Image cannot be transferred exclusively while another pane still owns
    // the same-URL job. Keep the shared-job path unchanged in that case.
    if (!exactClaim || validClaims.length !== 1 || job.claims.size !== 1) return null;

    let resolveReady;
    const ready = new Promise(resolve => { resolveReady = resolve; });
    const transfer = {
      paneId: exactClaim.paneId,
      generation: exactClaim.generation,
      kind: exactClaim.kind,
      url: exactClaim.url,
      settled: false,
      settle(image) {
        if (transfer.settled) return false;
        transfer.settled = true;
        linkedIntentTransfers.delete(transfer);
        resolveReady(image || null);
        return true;
      },
      cancel() {
        if (transfer.settled || job.intentTransfer !== transfer) return false;
        const cancelIntentImage = job.cancelIntentImage;
        if (typeof cancelIntentImage === 'function') cancelIntentImage();
        else {
          const image = job.intentImage;
          job.intentImage = null;
          job.intentTransfer = null;
          if (image) releaseDetachedImage(image);
          transfer.settle(null);
        }
        return true;
      },
    };
    job.intentTransfer = transfer;
    linkedIntentTransfers.add(transfer);

    job.claims.delete(exactClaim.id);
    const keyClaims = claimsByPaneKind.get(exactClaim.paneKind);
    keyClaims?.delete(exactClaim);
    if (keyClaims && keyClaims.size === 0) claimsByPaneKind.delete(exactClaim.paneKind);
    activeJobs.delete(job);
    if (unsettledByUrl.get(key) === job) unsettledByUrl.delete(key);
    if (activeByUrl.get(key) === job) activeByUrl.delete(key);
    requestDrain();

    return Object.freeze({
      image: job.intentImage,
      ready,
      cancel: () => transfer.cancel(),
    });
  };

  const pickNextJob = ({ allowedKind = null } = {}) => {
    const choices = [];
    let minimumDistance = Number.MAX_SAFE_INTEGER;
    for (const job of pendingByUrl.values()) {
      if (unsettledByUrl.has(job.url)) continue;
      const allValidClaims = validClaimsFor(job);
      for (const claim of [...job.claims.values()]) {
        if (!allValidClaims.includes(claim)) removeClaim(claim);
      }
      const validClaims = allowedKind == null
        ? allValidClaims
        : allValidClaims.filter(claim => claim.kind === allowedKind);
      if (!validClaims.length || !pendingByUrl.has(job.url)) continue;
      const distance = Math.min(...validClaims.map(claim => claim.distance));
      if (distance < minimumDistance) minimumDistance = distance;
      choices.push({ job, validClaims, distance });
    }
    const nearest = choices.filter(choice => choice.distance === minimumDistance);
    if (!nearest.length) return null;
    const paneIds = [...new Set(nearest.flatMap(choice => choice.validClaims
      .filter(claim => claim.distance === minimumDistance)
      .map(claim => claim.paneId)))].sort((left, right) => String(left).localeCompare(String(right), undefined, { numeric: true }));
    let paneIndex = 0;
    if (lastServedPane != null) {
      const prior = paneIds.findIndex(paneId => paneId === lastServedPane);
      paneIndex = prior < 0 ? 0 : (prior + 1) % paneIds.length;
    }
    const paneId = paneIds[paneIndex];
    const paneChoices = nearest
      .map(choice => ({
        ...choice,
        order: Math.min(...choice.validClaims
          .filter(claim => claim.paneId === paneId && claim.distance === minimumDistance)
          .map(claim => claim.order)),
      }))
      .filter(choice => Number.isFinite(choice.order))
      .sort((left, right) => left.order - right.order || left.job.sequence - right.job.sequence);
    lastServedPane = paneId;
    return paneChoices[0]?.job || null;
  };

  const hasPendingKind = kind => [...pendingByUrl.values()].some(job => (
    validClaimsFor(job).some(claim => claim.kind === kind)
  ));

  const hasActiveKind = kind => [...activeJobs].some(job => (
    validClaimsFor(job).some(claim => claim.kind === kind)
  ));

  const hasLinkedIntentWork = () => (
    hasPendingKind('linked-intent') || hasActiveKind('linked-intent')
  );
  const hasProjectedBurstWork = () => (
    hasPendingKind('burst-projected') || hasActiveKind('burst-projected')
  );

  const linkedIntentPaneDemand = () => {
    const paneIds = new Set();
    [...pendingByUrl.values(), ...activeJobs].forEach(job => {
      validClaimsFor(job).forEach(claim => {
        if (claim.kind === 'linked-intent') paneIds.add(claim.paneId);
      });
    });
    return paneIds.size;
  };

  const currentSpeculativeCapacity = () => (
    hasLinkedIntentWork()
      ? Math.max(concurrency, CINE_RESOURCE_LINKED_INTENT_CONCURRENCY, linkedIntentPaneDemand())
      : (hasProjectedBurstWork()
        ? Math.max(concurrency, CINE_RESOURCE_BURST_PROJECTED_CONCURRENCY)
        : concurrency)
  );

  const requestDrain = ({ immediateLinked = false } = {}) => {
    const visibleWorkActive = visibleByPane.size > 0;
    const projectedBurstPending = hasPendingKind('burst-projected');
    if (destroyed || drainScheduled || activeJobs.size >= currentSpeculativeCapacity() || pendingByUrl.size === 0
        || (visibleWorkActive && !hasPendingKind('linked-intent') && !projectedBurstPending)) return;
    drainScheduled = true;
    const scheduledEpoch = speculativeEpoch;
    const drain = () => {
      drainScheduled = false;
      if (scheduledEpoch !== speculativeEpoch) return;
      if (destroyed || activeJobs.size >= currentSpeculativeCapacity()) return;
      const allowedKind = hasLinkedIntentWork()
        ? 'linked-intent'
        : (visibleByPane.size > 0 && hasPendingKind('burst-projected') ? 'burst-projected' : null);
      const job = pickNextJob({ allowedKind });
      if (!job) return;
      pendingByUrl.delete(job.url);
      job.epoch = scheduledEpoch;
      unsettledByUrl.set(job.url, job);
      activeByUrl.set(job.url, job);
      activeJobs.add(job);
      const ownsActiveClaim = () => [...job.claims.values()].some(claimIsCurrent);
      const ownsActiveJob = () => (
        activeJobs.has(job)
        && activeByUrl.get(job.url) === job
        && job.epoch === speculativeEpoch
        && !job.cancelled
        && ownsActiveClaim()
      );
      const finish = (success, { rememberCompletion = true } = {}) => {
        const completedWhileOwned = success === true && ownsActiveJob();
        const currentClaims = !job.cancelled ? validClaimsFor(job) : [];
        const promotedClaims = job.runKind === 'ordinary'
          && currentClaims.some(claim => claim.kind === 'linked-intent')
          ? currentClaims
          : [];
        if (!activeJobs.delete(job)) return;
        if (unsettledByUrl.get(job.url) === job) unsettledByUrl.delete(job.url);
        if (activeByUrl.get(job.url) === job) activeByUrl.delete(job.url);
        if (promotedClaims.length > 0) {
          const replacement = {
            url: job.url,
            claims: new Map(promotedClaims.map(claim => [claim.id, claim])),
            sequence: jobSequence += 1,
            controller: null,
            cancelled: false,
          };
          pendingByUrl.set(job.url, replacement);
          job.claims.clear();
          requestDrain();
          return;
        }
        if (completedWhileOwned && rememberCompletion) {
          rememberCompletedUrl(
            job.url,
            job.preloadProducerClaim ? (job.preloadCompletionEntry || null) : undefined,
          );
        }
        for (const claim of job.claims.values()) {
          const keyClaims = claimsByPaneKind.get(claim.paneKind);
          keyClaims?.delete(claim);
          if (keyClaims && keyClaims.size === 0) claimsByPaneKind.delete(claim.paneKind);
        }
        job.claims.clear();
        requestDrain();
      };
      const preloadProducerClaim = validClaimsFor(job).find(claim => claim.evidencePreloadContext);
      if (preloadProducerClaim) {
        job.runKind = 'evidence-preload';
        job.preloadProducerClaim = preloadProducerClaim;
        const preloadContext = preloadProducerClaim.evidencePreloadContext;
        let operation = null;
        try {
          operation = beginEvidencePreloadRequest({
            benchmarkMode: true,
            context: preloadContext.context,
            requestUrl: job.url,
            currentOrigin: preloadContext.currentOrigin,
            fetchImpl: preloadContext.fetchImpl,
            blobFactory: preloadContext.blobFactory,
            createObjectURL: preloadContext.createObjectURL,
            revokeObjectURL: preloadContext.revokeObjectURL,
            createImage: preloadContext.createImage,
            isMounted: preloadContext.isMounted,
            isViewportCurrent: preloadContext.isViewportCurrent,
            isGenerationCurrent: preloadContext.isGenerationCurrent,
            isSliceCurrent: preloadContext.isSliceCurrent,
            isRenderAuthorityCurrent: preloadContext.isRenderAuthorityCurrent,
          });
        } catch (_) {
          operation = null;
        }
        if (!operation || typeof operation.ready !== 'function'
            || typeof operation.registerPreloadDecodedCacheRoot !== 'function'
            || typeof operation.cancel !== 'function') {
          try { operation?.cancel?.(); } catch (_) {}
          finish(false);
          return;
        }
        job.bridgeOperation = operation;
        Promise.resolve(operation.ready()).then(ready => {
          if (ready !== true || !ownsActiveJob()
              || !job.claims.has(preloadProducerClaim.id)) {
            cancelBridgeOperation(job);
            finish(false);
            return;
          }
          let registered = false;
          try {
            registered = operation.registerPreloadDecodedCacheRoot({
              canonicalUrl: job.url,
              register: (root, image) => {
                if (!ownsActiveJob() || !job.claims.has(preloadProducerClaim.id)) return null;
                const detach = registerPreloadedDecodedEvidenceRoot(job.url, image, root);
                const entry = decoded.get(job.url);
                if (typeof detach === 'function' && entry?.preloadDetach === detach
                    && entry.image === image && entry.evidenceRoot === root
                    && entry.evidenceRootNotified === false) job.preloadCompletionEntry = entry;
                return detach;
              },
            }) === true;
          } catch (_) {
            registered = false;
          }
          if (!registered) {
            cancelBridgeOperation(job);
          }
          finish(registered);
        }, () => {
          cancelBridgeOperation(job);
          finish(false);
        });
        requestDrain();
        return;
      }
      const linkedIntentClaim = validClaimsFor(job).find(claim => claim.kind === 'linked-intent');
      if (linkedIntentClaim) {
        job.runKind = 'linked-intent';
        const hasCurrentLinkedIntentClaim = () => (
          validClaimsFor(job).some(claim => claim.kind === 'linked-intent')
        );
        let image;
        try {
          image = createSpeculativeImage();
        } catch (_) {
          image = null;
        }
        if (!image) {
          finish(false);
          return;
        }
        job.intentImage = image;
        let terminal = false;
        const settleIntentImage = decodedOk => {
          if (terminal) return;
          terminal = true;
          job.cancelIntentImage = null;
          if (job.intentImage === image) job.intentImage = null;
          try { image.onload = null; } catch (_) {}
          try { image.onerror = null; } catch (_) {}
          const transfer = job.intentTransfer;
          if (transfer) {
            job.intentTransfer = null;
            if (decodedOk === true) transfer.settle(image);
            else {
              releaseDetachedImage(image);
              transfer.settle(null);
            }
            return;
          }
          if (decodedOk !== true || !ownsActiveJob()
              || !hasCurrentLinkedIntentClaim()) {
            releaseDetachedImage(image);
            finish(false);
            return;
          }
          const retained = retainDecoded({ url: job.url, image });
          if (!retained.retained) releaseDetachedImage(image);
          finish(retained.retained === true, { rememberCompletion: false });
        };
        job.cancelIntentImage = () => settleIntentImage(false);
        image.onload = () => {
          if (!job.intentTransfer && (!ownsActiveJob() || !hasCurrentLinkedIntentClaim())) {
            settleIntentImage(false);
            return;
          }
          // Do not call HTMLImageElement.decode() for LINK intent. The browser's
          // load event is the same renderable boundary used by the proven direct
          // visible path. Starting an explicit detached decode here can lock the
          // exact image while accepted authority is ready, forcing a skipped
          // follower frame or duplicate transport. Intent remains non-visible;
          // only accepted authority may consume the retained/transfer-owned image.
          settleIntentImage(true);
        };
        image.onerror = () => settleIntentImage(false);
        try {
          image.decoding = 'async';
          image.fetchPriority = 'high';
          image.src = job.url;
        } catch (_) {
          settleIntentImage(false);
          return;
        }
        // Once one LINK intent has entered its scheduled drain, launch the
        // remaining exact follower intents in this same event-loop turn. With
        // three followers, chaining setTimeout(0) admissions can consume the
        // entire source→accepted lead and make later panes start one command
        // behind. This immediate continuation is bounded by the dedicated
        // LINK concurrency cap; ordinary speculative and triangulation work
        // remain scheduled through scheduleDrain.
        requestDrain({ immediateLinked: true });
        return;
      }
      job.runKind = 'ordinary';
      const controller = createAbortController();
      job.controller = controller;
      let request;
      try {
        request = fetchImpl(job.url, {
          credentials: 'same-origin',
          cache: 'force-cache',
          headers: { 'X-Kairos-Render-Intent': 'speculative' },
          signal: controller.signal,
        });
      } catch (_) {
        finish(false);
        return;
      }
      Promise.resolve(request)
        .then(async response => {
          if (!ownsActiveJob()) {
            try { await response?.body?.cancel?.(); } catch (_) {}
            return false;
          }
          if (!response?.ok) {
            const status = Number(response?.status);
            if ((status === 401 || status === 403) && activeByUrl.get(job.url) === job) {
              reset();
              try { onAuthorizationFailure?.({ status }); } catch (_) {}
              return false;
            }
            throw new Error('speculative request failed');
          }
          const reader = response.body?.getReader?.();
          if (!reader || typeof reader.read !== 'function') return false;
          job.reader = reader;
          let reachedTerminalDone = false;
          let observedPositiveBytes = false;
          try {
            while (ownsActiveJob()) {
              const result = await reader.read();
              if (!ownsActiveJob()) return false;
              if (result?.done === true) {
                reachedTerminalDone = true;
                break;
              }
              if (result?.value instanceof Uint8Array && result.value.byteLength > 0) {
                observedPositiveBytes = true;
              }
            }
          } finally {
            try { reader.releaseLock?.(); } catch (_) {}
            job.reader = null;
          }
          return observedPositiveBytes && reachedTerminalDone && ownsActiveJob();
        })
        .then(success => finish(success === true), () => finish(false));
      requestDrain();
    };
    if (immediateLinked === true && hasPendingKind('linked-intent')) drain();
    else scheduleDrain(drain);
  };

  const beginVisibleInteraction = ({ preserveSpeculativeKinds = [] } = {}) => {
    if (destroyed) return false;
    preemptSpeculativeExceptKinds(preserveSpeculativeKinds);
    return true;
  };

  const beginVisible = ({
    paneId,
    generation,
    direction,
    url,
    preemptSpeculative = true,
    preserveSpeculativeKinds = [],
  } = {}) => {
    if (destroyed) return false;
    if (preemptSpeculative !== false) preemptSpeculativeExceptKinds(preserveSpeculativeKinds);
    paneState.set(paneId, {
      ...(paneState.get(paneId) || {}),
      generation,
      direction: normalizedDirection(direction),
    });
    visibleByPane.set(paneId, { generation, direction: normalizedDirection(direction), url: String(url || '') });
    return true;
  };

  const settleVisible = ({ paneId, generation, url } = {}) => {
    if (destroyed) return false;
    const current = visibleByPane.get(paneId);
    if (!current || current.generation !== generation || current.url !== String(url || '')) return false;
    visibleByPane.delete(paneId);
    requestDrain();
    return true;
  };

  const replaceSpeculative = ({ paneId, generation, kind, direction, candidates = [] } = {}) => {
    if (destroyed) return { accepted: 0, deduplicated: 0 };
    removeSpeculative({ paneId, kind });
    if (paneState.get(paneId)?.generation !== generation || !Array.isArray(candidates)) {
      return { accepted: 0, deduplicated: 0 };
    }
    const key = paneKindKey(paneId, kind);
    const keyClaims = new Set();
    claimsByPaneKind.set(key, keyClaims);
    let accepted = 0;
    let duplicateClaims = 0;
    const seen = new Set();
    candidates.forEach((candidate, candidateIndex) => {
      const url = String(candidate?.url || '');
      if (!url || seen.has(url)) return;
      seen.add(url);
      const preloadInspection = inspectCandidateEvidencePreloadContext(candidate);
      let evidencePreloadContext = null;
      if (preloadInspection.supplied) {
        let preloadActive = false;
        try {
          preloadActive = typeof isEvidencePreloadActive === 'function'
            && isEvidencePreloadActive() === true;
        } catch (_) {
          preloadActive = false;
        }
        if (preloadActive) {
          if (!preloadInspection.snapshot) return;
          evidencePreloadContext = preloadInspection.snapshot;
        }
      }
      if (hasCompletedUrl(url)) {
        duplicateClaims += 1;
        deduplicated += 1;
        return;
      }
      let job = pendingByUrl.get(url);
      if (!job) {
        const activeJob = activeByUrl.get(url);
        if (activeJob && !activeJob.cancelled) job = activeJob;
      }
      if (job) {
        duplicateClaims += 1;
        deduplicated += 1;
      } else {
        job = { url, claims: new Map(), sequence: jobSequence += 1, controller: null, cancelled: false };
        pendingByUrl.set(url, job);
      }
      const claim = {
        id: Symbol(),
        paneId,
        paneKind: key,
        generation,
        kind,
        direction: normalizedDirection(direction),
        distance: normalizedDistance(candidate?.distance),
        order: normalizedOrder(candidate?.order ?? candidateIndex),
        url,
        evidencePreloadContext,
      };
      job.claims.set(claim.id, claim);
      keyClaims.add(claim);
      accepted += 1;
    });
    if (keyClaims.size === 0) claimsByPaneKind.delete(key);
    requestDrain();
    return { accepted, deduplicated: duplicateClaims };
  };

  const reconcileSpeculative = ({ paneId, generation, kind, direction, candidates = [] } = {}) => {
    if (destroyed) return { accepted: 0, deduplicated: 0, preserved: 0 };
    if (paneState.get(paneId)?.generation !== generation || !Array.isArray(candidates)) {
      return { accepted: 0, deduplicated: 0, preserved: 0 };
    }
    const key = paneKindKey(paneId, kind);
    const priorClaims = [...(claimsByPaneKind.get(key) || [])];
    const priorByUrl = new Map(priorClaims.map(claim => [claim.url, claim]));
    const retainedClaims = new Set();
    const nextDirection = normalizedDirection(direction);
    let accepted = 0;
    let duplicateClaims = 0;
    let preserved = 0;
    const seen = new Set();

    candidates.forEach((candidate, candidateIndex) => {
      const url = String(candidate?.url || '');
      if (!url || seen.has(url)) return;
      seen.add(url);
      const preloadInspection = inspectCandidateEvidencePreloadContext(candidate);
      let evidencePreloadContext = null;
      if (preloadInspection.supplied) {
        let preloadActive = false;
        try {
          preloadActive = typeof isEvidencePreloadActive === 'function'
            && isEvidencePreloadActive() === true;
        } catch (_) {
          preloadActive = false;
        }
        if (preloadActive) {
          if (!preloadInspection.snapshot) return;
          evidencePreloadContext = preloadInspection.snapshot;
        }
      }
      if (hasCompletedUrl(url)) {
        duplicateClaims += 1;
        deduplicated += 1;
        return;
      }

      const priorClaim = priorByUrl.get(url);
      if (priorClaim && priorClaim.direction !== nextDirection) {
        // A same-URL reversal is not overlap: the active producer still carries
        // the old product evidence context. Remove it before admitting the new
        // direction so the old job is aborted rather than lending stale
        // authority to the reversed claim.
        priorByUrl.delete(url);
        removeClaim(priorClaim);
      }
      if (priorClaim && priorClaim.direction === nextDirection) {
        const job = pendingByUrl.get(url) || activeByUrl.get(url);
        priorClaim.generation = generation;
        priorClaim.direction = nextDirection;
        priorClaim.distance = normalizedDistance(candidate?.distance);
        priorClaim.order = normalizedOrder(candidate?.order ?? candidateIndex);
        if (pendingByUrl.get(url) === job) priorClaim.evidencePreloadContext = evidencePreloadContext;
        retainedClaims.add(priorClaim);
        accepted += 1;
        duplicateClaims += 1;
        deduplicated += 1;
        preserved += 1;
        return;
      }

      let job = pendingByUrl.get(url);
      if (!job) {
        const activeJob = activeByUrl.get(url);
        if (activeJob && !activeJob.cancelled) job = activeJob;
      }
      if (job) {
        duplicateClaims += 1;
        deduplicated += 1;
      } else {
        job = { url, claims: new Map(), sequence: jobSequence += 1, controller: null, cancelled: false };
        pendingByUrl.set(url, job);
      }
      const claim = {
        id: Symbol(),
        paneId,
        paneKind: key,
        generation,
        kind,
        direction: nextDirection,
        distance: normalizedDistance(candidate?.distance),
        order: normalizedOrder(candidate?.order ?? candidateIndex),
        url,
        evidencePreloadContext,
      };
      job.claims.set(claim.id, claim);
      retainedClaims.add(claim);
      accepted += 1;
    });

    priorClaims.forEach(claim => {
      if (!retainedClaims.has(claim)) removeClaim(claim);
    });
    if (retainedClaims.size > 0) claimsByPaneKind.set(key, retainedClaims);
    else claimsByPaneKind.delete(key);
    requestDrain();
    return { accepted, deduplicated: duplicateClaims, preserved };
  };

  return {
    registerPane,
    fencePaneGeneration,
    beginVisibleInteraction,
    beginVisible,
    settleVisible,
    replaceSpeculative,
    reconcileSpeculative,
    removeSpeculative,
    takeLinkedIntentImage,
    retainDecoded,
    getDecoded,
    registerDecodedEvidenceRoot,
    registerPreloadedDecodedEvidenceRoot,
    getDecodedEvidenceRoot,
    evictDecodedEvidenceRoot,
    retryRootCleanup,
    releaseDetachedImage,
    reset,
    destroy,
    snapshot: () => ({
      paneCount: [...paneState.values()].filter(state => state.registration).length,
      visibleActive: visibleByPane.size,
      speculativeQueued: pendingByUrl.size,
      speculativeActive: activeJobs.size,
      linkedIntentTransfers: linkedIntentTransfers.size,
      linkedIntentPending: [...pendingByUrl.values()].filter(job => validClaimsFor(job).some(claim => claim.kind === 'linked-intent')).length,
      linkedIntentActive: [...activeJobs].filter(job => validClaimsFor(job).some(claim => claim.kind === 'linked-intent')).length,
      decodedEntries: decoded.size,
      decodedBytes,
      activeCleanup: activeCleanupCount(),
      pendingCleanup: failedRootNotifications.size,
      cleanupCapacity,
      completedUrlEntries: completedUrls.size,
      completedUrlBytes,
      aborts: aborted,
      deduplicated,
      hits: cacheHits,
      misses: cacheMisses,
    }),
  };
};
